/**
 * Vstup na portál Tabletop. Účet patrí portálu, nie konkrétnej hre.
 *
 * - **Registrácia**: kód z pozvánky + meno + heslo. Kód sa tým spotrebuje.
 * - **Prihlásenie**: už len meno a heslo.
 *
 * Hráči spred zavedenia hesiel majú kód naviazaný na účet bez hesla —
 * „zaregistrujú" sa svojím kódom a svojím menom a nastavia si heslo.
 */

import { useState } from 'react';

import { ApiError, auth, type SessionUser } from '../api.js';

interface Props {
  onSignedIn: (user: SessionUser) => void;
}

/** Priebežné formátovanie na XXX-XXX-XXX-XXX, nech sa to dobre píše. */
function formatCode(input: string): string {
  const raw = input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  return (raw.match(/.{1,3}/g) ?? []).join('-');
}

const PASSWORD_MIN = 8;

export function Gate({ onSignedIn }: Props) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [code, setCode] = useState('');
  const [nick, setNick] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codeComplete = code.replace(/-/g, '').length === 12;
  const ready =
    mode === 'login'
      ? nick.trim().length >= 2 && password.length > 0
      : codeComplete && nick.trim().length >= 2 && password.length >= PASSWORD_MIN && password === again;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } =
        mode === 'login'
          ? await auth.login(nick.trim(), password)
          : await auth.register(code, nick.trim(), password);
      onSignedIn(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nepodarilo sa prihlásiť');
    } finally {
      setBusy(false);
    }
  }

  function switchTo(next: 'login' | 'register') {
    setMode(next);
    setError(null);
    setPassword('');
    setAgain('');
  }

  return (
    // Vstup má vzhľad portálu (Hearth & Table), nie arény Unmatched.
    <div className="gate shell--portal">
      <form className="gate__card" onSubmit={submit}>
        <img
          className="gate__emblem"
          src="/brand/hearth-table-512.webp"
          alt="Hearth & Table"
          width={132}
          height={132}
          draggable={false}
        />
        <h1 className="gate__title">
          {mode === 'login' ? (
            <>
              Vitaj <em>späť</em>
            </>
          ) : (
            <>
              Nový <em>účet</em>
            </>
          )}
        </h1>

        <div className="segment gate__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={`segment__item ${mode === 'login' ? 'is-active' : ''}`}
            onClick={() => switchTo('login')}
          >
            Prihlásenie
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={`segment__item ${mode === 'register' ? 'is-active' : ''}`}
            onClick={() => switchTo('register')}
          >
            Registrácia
          </button>
        </div>

        <p className="gate__lead">
          {mode === 'login'
            ? 'Prihlás sa menom a heslom. Účet platí pre všetky hry na portáli.'
            : 'Registrácia je len s pozývacím kódom. Kód použiješ raz — potom sa prihlasuješ menom a heslom. Ak si tu hral ešte pred heslami, zadaj svoj kód a svoje meno a nastav si heslo.'}
        </p>

        {mode === 'register' ? (
          <>
            <label className="gate__label" htmlFor="code">
              Pozývací kód
            </label>
            <input
              id="code"
              className="gate__code"
              value={code}
              onChange={(e) => setCode(formatCode(e.target.value))}
              placeholder="XXX-XXX-XXX-XXX"
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </>
        ) : null}

        <label className="gate__label" htmlFor="nick">
          Meno
        </label>
        <input
          id="nick"
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          placeholder="napr. Jano"
          maxLength={24}
          autoComplete="username"
          autoFocus={mode === 'login'}
        />

        <label className="gate__label" htmlFor="password">
          Heslo {mode === 'register' ? <span className="gate__hint">(aspoň {PASSWORD_MIN} znakov)</span> : null}
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        />

        {mode === 'register' ? (
          <>
            <label className="gate__label" htmlFor="again">
              Heslo znova
            </label>
            <input
              id="again"
              type="password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              autoComplete="new-password"
            />
            {again && again !== password ? <div className="gate__hint">Heslá sa nezhodujú.</div> : null}
          </>
        ) : null}

        {error ? <div className="gate__error">{error}</div> : null}

        <button type="submit" className="btn btn--accent btn--lg" disabled={!ready || busy}>
          {busy ? 'Chvíľu…' : mode === 'login' ? 'Prihlásiť sa' : 'Zaregistrovať sa'}
        </button>
      </form>
    </div>
  );
}
